import { Form } from 'factorial-form'
import {
  ContractFlowCustomFieldFragment,
  ContractFlowCustomFieldValueFragment,
  FixFlowProviderContractVersionFragment,
  PayrollIntegrationsBaseA3innuvaCodesQuery,
} from 'generated/resources/graphql'

import {
  endsOnField,
  hasTrialPeriodField,
  jobTitleField,
  salaryFields,
  startsOnField,
  trialPeriodEndDateField,
  workingHoursFields,
} from 'modules/contracts/lib/checkFieldVisibility'
import { customFieldsFormKeys, customFieldsToFormValues } from 'modules/contracts/lib/customFields'

type PayrollIntegrationsBaseCode = NonNullable<
  NonNullable<
    NonNullable<
      PayrollIntegrationsBaseA3innuvaCodesQuery['payrollIntegrationsBase']
    >['codesConnection']
  >['nodes']
>[number]

const contractVersionToFormValues = (contractVersion: FixFlowProviderContractVersionFragment) => {
  // Helper to convert null to undefined for number fields (factorial-form doesn't accept null for number fields)
  const toNumberOrUndefined = (value: number | null | undefined): number | undefined => {
    return value ?? undefined
  }

  // Helper to convert null to undefined for string ID fields (factorial-form doesn't accept null for string fields)
  const toStringOrUndefined = (value: string | null | undefined): string | undefined => {
    return value ?? undefined
  }

  return {
    // Base fields
    annual_working_time_distribution: contractVersion.annualWorkingTimeDistribution,
    bank_holiday_treatment: contractVersion.bankHolidayTreatment,
    effective_on: contractVersion.effectiveOn,
    ends_on: contractVersion.endsOn,
    has_trial_period: contractVersion.hasTrialPeriod,
    id: toStringOrUndefined(contractVersion.id),
    job_title: contractVersion.jobTitle,
    payroll_policy_id: toStringOrUndefined(contractVersion.payrollPolicy?.policy?.id),
    starts_on: contractVersion.startsOn,
    trial_period_ends_on: contractVersion.trialPeriodEndsOn,
    working_time_percentage_in_cents: toNumberOrUndefined(
      contractVersion.workingTimePercentageInCents
    ),

    // Job Catalog fields
    job_catalog_level_id: toStringOrUndefined(contractVersion.jobCatalogLevel?.id),
    job_catalog_role_id: toStringOrUndefined(contractVersion.jobCatalogLevel?.role?.id),
    job_catalog_tree_node_uuid: contractVersion.jobCatalogTreeNodeUuid,

    // Salary fields
    // We need to convert the salary amount to a number because the form field is typed as a number
    // and the graphql salaryAmount is typed as BigInt and returning a string
    salary_amount: Number(contractVersion.salaryAmount) || undefined,
    salary_frequency: contractVersion.salaryFrequency,

    // Working Hours fields
    max_legal_yearly_hours: toNumberOrUndefined(contractVersion.maxLegalYearlyHours),
    maximum_weekly_hours: toNumberOrUndefined(contractVersion.maximumWeeklyHours),
    max_work_minutes_per_day: toNumberOrUndefined(contractVersion.maxWorkMinutesPerDay),
    min_rest_minutes_between_days: toNumberOrUndefined(contractVersion.minRestMinutesBetweenDays),
    working_hours: toNumberOrUndefined(contractVersion.workingHours),
    working_hours_frequency: contractVersion.workingHoursFrequency,
    working_week_days: contractVersion.workingWeekDays,

    // Spain fields
    es_contract_type_id: toStringOrUndefined(contractVersion.esContractTypeId),
    es_contribution_type_id: toStringOrUndefined(contractVersion.esContributionTypeId),
    es_cotization_group: toNumberOrUndefined(contractVersion.esCotizationGroup),
    es_education_level_id: toStringOrUndefined(contractVersion.esEducationLevelId),
    es_agreement_code_id: toStringOrUndefined(contractVersion.esAgreementCodeId),
    es_cno_occupation_id: toStringOrUndefined(contractVersion.esCnoOccupationId),
    es_contract_observations: contractVersion.esContractObservations,
    es_has_teleworking_contract: contractVersion.esHasTeleworkingContract,
    es_professional_category_id: toStringOrUndefined(contractVersion.esProfessionalCategoryId),
    es_job_description: contractVersion.esJobDescription,
    es_working_day_type_id: toStringOrUndefined(contractVersion.esWorkingDayTypeId),
    es_regime_id: toStringOrUndefined(contractVersion.esRegimeId),
    es_tariff_group_id: toStringOrUndefined(contractVersion.esTariffGroupId),
    es_tariff_group_name: contractVersion.esTariffGroupName,
    es_occupation_code_id: toStringOrUndefined(contractVersion.esOccupationCodeId),
    es_classification_id: toStringOrUndefined(contractVersion.esClassificationId),
    es_a3innuva_job_position_id: toStringOrUndefined(contractVersion.esA3innuvaJobPositionId),

    // France fields
    fr_contract_type_id: toStringOrUndefined(contractVersion.frContractTypeId),
    fr_employee_type: contractVersion.frEmployeeType,
    fr_forfait_jours: contractVersion.frForfaitJours,
    fr_jours_par_an: toNumberOrUndefined(contractVersion.frJoursParAn),
    fr_jours_par_an_cents: toNumberOrUndefined(contractVersion.frJoursParAnCents),
    fr_level_id: toStringOrUndefined(contractVersion.frLevelId),
    fr_mutual_id: toStringOrUndefined(contractVersion.frMutualId),
    fr_professional_category_id: toStringOrUndefined(contractVersion.frProfessionalCategoryId),
    fr_step_id: toStringOrUndefined(contractVersion.frStepId),
    fr_work_type_id: toStringOrUndefined(contractVersion.frWorkTypeId),
    fr_coefficient: contractVersion.frCoefficient,

    // Germany fields
    de_contract_type_id: toStringOrUndefined(contractVersion.deContractTypeId),
    de_employment_type: toNumberOrUndefined(contractVersion.deEmploymentType),
    de_flat_rate_tax: toNumberOrUndefined(contractVersion.deFlatRateTax),
    de_activity_type: toNumberOrUndefined(contractVersion.deActivityType),
    de_personal_key_group_id: toStringOrUndefined(contractVersion.dePersonalKeyGroupId),
    de_base_salary_type_id: toStringOrUndefined(contractVersion.deBaseSalaryTypeId),

    // Portugal fields
    pt_contract_type_id: toStringOrUndefined(contractVersion.ptContractTypeId),
  }
}

const baseCodesToFormValues = (baseCodes: PayrollIntegrationsBaseCode[]) => {
  return baseCodes.reduce((formValues: { [key: string]: string | number }, baseCode) => {
    const baseCodeKey = getBaseCodeKey(baseCode)
    if (baseCodeKey) {
      formValues[baseCodeKey] = baseCode.code
    }
    return formValues
  }, {})
}

// TODO: Regarding the integrations installed, we should only fetch the ones that applied to those integrations, but for now we fetch always the ones for a3innuva
const getBaseCodeKey = (baseCode: PayrollIntegrationsBaseCode) => {
  switch (baseCode.codeableType) {
    case 'Contracts::Spain::AgreementCode':
      return 'es_agreement_code_payroll_integration_code'
    case 'Contracts::Spain::ContractType':
      return 'es_contract_type_payroll_integration_code'
    case 'Contracts::Spain::ContributionType':
      return 'es_contribution_type_payroll_integration_code'
    case 'Contracts::Spain::EducationLevel':
      return 'es_education_level_payroll_integration_code'
    case 'Contracts::Spain::CnoOccupation':
      return 'es_cno_occupation_payroll_integration_code'
    case 'Contracts::Spain::Regime':
      return 'es_regime_payroll_integration_code'
    case 'Contracts::Spain::OccupationCode':
      return 'es_occupation_code_payroll_integration_code'
    case 'Contracts::Spain::Classification':
      return 'es_classification_payroll_integration_code'
    case 'Contracts::Spain::A3innuvaJobPosition':
      return 'es_a3innuva_job_position_payroll_integration_code'
    default:
      return null
  }
}

interface Props {
  contractVersion: FixFlowProviderContractVersionFragment
  customFields: ContractFlowCustomFieldFragment[]
  customValues: ContractFlowCustomFieldValueFragment[]
  baseCodes: PayrollIntegrationsBaseCode[]
  canSeeJobTitle: boolean
  canSeeStartDate: boolean
  canSeeEndDate: boolean
  canSeeTrialPeriod: boolean
  canSeeTrialPeriodEndDate: boolean
  canSeeGrossSalary: boolean
  canSeeWorkingHours: boolean
}

export const getFixFlowForm = ({
  contractVersion,
  customFields,
  customValues,
  baseCodes,
  canSeeJobTitle,
  canSeeStartDate,
  canSeeEndDate,
  canSeeTrialPeriod,
  canSeeTrialPeriodEndDate,
  canSeeGrossSalary,
  canSeeWorkingHours,
}: Props): Form => {
  return new Form(
    {
      ...contractVersionToFormValues(contractVersion),
      ...customFieldsToFormValues(customFields, customValues),
      ...baseCodesToFormValues(baseCodes),
    },
    {
      ...jobTitleField(canSeeJobTitle),
      id: 'string',
      bank_holiday_treatment: 'string',
      working_time_percentage_in_cents: 'number',
      annual_working_time_distribution: 'string',
      effective_on: 'date',
      ends_on: 'date',
      es_contract_type_id: 'string',
      es_contract_type_payroll_integration_code: 'string',
      es_contract_type_payroll_integration_name: 'string',
      es_contribution_type_id: 'string',
      es_contribution_type_payroll_integration_code: 'string',
      es_contribution_type_payroll_integration_name: 'string',
      es_cotization_group: 'number',
      es_education_level_payroll_integration_code: 'string',
      es_education_level_payroll_integration_name: 'string',
      es_education_level_id: 'string',
      es_agreement_code_id: 'string',
      es_agreement_code_payroll_integration_code: 'string',
      es_agreement_code_payroll_integration_name: 'string',
      es_cno_occupation_id: 'string',
      es_cno_occupation_payroll_integration_code: 'string',
      es_cno_occupation_payroll_integration_name: 'string',
      es_contract_observations: 'string',
      es_has_teleworking_contract: 'boolean',
      es_professional_category_id: 'string',
      es_job_description: 'string',
      es_working_day_type_id: 'string',
      es_regime_id: 'string',
      es_regime_payroll_integration_code: 'string',
      es_regime_payroll_integration_name: 'string',
      es_tariff_group_id: 'string',
      es_tariff_group_name: 'string',
      es_occupation_code_id: 'string',
      es_occupation_code_payroll_integration_code: 'string',
      es_occupation_code_payroll_integration_name: 'string',
      es_classification_id: 'string',
      es_classification_payroll_integration_code: 'string',
      es_classification_payroll_integration_name: 'string',
      es_a3innuva_job_position_id: 'string',
      es_a3innuva_job_position_payroll_integration_code: 'string',
      es_a3innuva_job_position_payroll_integration_name: 'string',
      fr_contract_type_id: 'string',
      fr_employee_type: 'string',
      fr_forfait_jours: 'boolean',
      fr_jours_par_an: 'number',
      fr_jours_par_an_cents: 'cents',
      fr_level_id: 'string',
      fr_mutual_id: 'string',
      fr_professional_category_id: 'string',
      fr_step_id: 'string',
      fr_work_type_id: 'string',
      fr_coefficient: 'string',
      de_contract_type_id: 'string',
      pt_contract_type_id: 'string',
      job_catalog_level_id: 'string',
      job_catalog_role_id: 'string',
      job_catalog_tree_node_uuid: 'string',
      job_catalog_role_level_name: 'string',
      payroll_policy_id: 'string',
      de_employment_type: 'number',
      de_flat_rate_tax: 'number',
      de_activity_type: 'number',
      de_personal_key_group_id: 'string',
      de_base_salary_type_id: 'string',
      ...salaryFields(canSeeGrossSalary),
      ...workingHoursFields(canSeeWorkingHours),
      ...hasTrialPeriodField(canSeeTrialPeriod),
      ...trialPeriodEndDateField(canSeeTrialPeriodEndDate),
      ...startsOnField(canSeeStartDate),
      ...endsOnField(canSeeEndDate),
      ...customFieldsFormKeys(customFields, customValues),
    }
  )
}
